<template>
  <section space-y-4>
    <h2 text-2xl font-semibold text-stone-500>Average Snowfall{{ name ? ` in ${name}` : "" }}</h2>
    <template v-if="normals">
      <div grid grid-cols-2 gap-2>
        <div
          v-for="stat in stats"
          :key="stat.label"
          rounded
          bg-stone-50
          p-3
          border-stone-100
          border-3
          flex
          flex-col
          justify-between
        >
          <div text-3xl font-semibold text-stone-700 tabular-nums>{{ stat.value }}</div>
          <div font-mono text-stone-500 lh-snug>{{ stat.label }}</div>
        </div>
      </div>

      <div py-4>
        <h2 text-xl font-semibold text-stone-400>Average Snowfall by Month</h2>

        <figure v-if="normals.months.length">
          <!-- the bars repeat the table below for sighted readers -->
          <div aria-hidden="true" flex items-end gap-2 sm:gap-3 h-40 pt-6 border-b border-stone-300>
            <div
              v-for="month in normals.months"
              :key="month.month"
              class="group"
              flex-1
              h-full
              flex
              flex-col
              justify-end
              items-center
              relative
              :title="`${month.month}: ${formatInches(month.inches)}`"
            >
              <span
                class="absolute text-stone-400 font-bold text-xs sm:text-sm tabular-nums whitespace-nowrap group-hover:text-stone-600"
                :class="month.month === normals.snowiest?.month ? '' : 'hidden group-hover:block'"
                :style="{ bottom: `calc(${barHeight(month.inches)}% + 4px)` }"
              >
                {{ formatInches(month.inches) }}
              </span>
              <div
                w-full
                rounded-t
                bg-brand-blue
                group-hover:bg-blue-500
                :style="{ height: `${barHeight(month.inches)}%` }"
              />
            </div>
          </div>
          <div aria-hidden="true" flex gap-1 sm:gap-3 pt-1>
            <div v-for="month in normals.months" :key="month.month" flex-1 text-center text-sm font-mono text-stone-500>
              {{ month.month.slice(0, 3) }}
            </div>
          </div>
          <table sr-only>
            <caption>
              Average snowfall by month{{
                name ? ` in ${name}` : ""
              }}
            </caption>
            <thead>
              <tr>
                <th scope="col">Month</th>
                <th scope="col">Average snowfall</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="month in normals.months" :key="month.month">
                <th scope="row">{{ month.month }}</th>
                <td>{{ formatInches(month.inches) }}</td>
              </tr>
            </tbody>
          </table>
        </figure>
      </div>
    </template>

    <p v-if="description" prose lg:text-lg>{{ description }}</p>
    <p v-if="normals">
      Averages are the NOAA 1991–2020
      <a href="https://www.ncei.noaa.gov/products/land-based-station/us-climate-normals" font-semibold
        >climate normals</a
      >
      for {{ stationDescription }}.
    </p>
  </section>
</template>
<script setup>
const props = defineProps({
  // from /api/snow/place; null where no station is close enough
  normals: { type: Object, default: null },
  // the place's short name, e.g. "Buffalo"
  name: { type: String, default: null },
  // from /api/snow/place, for describing the place
  page: { type: Object, default: null },
  county: { type: Object, default: null },
  state: { type: Object, default: null },
  // the share of the state's towns that get less snow, 0 to 1
  stateShare: { type: Number, default: null },
});

function formatInches(inches) {
  return `${numberFormatted(inches, { decimals: 1 })} in`;
}

function formatDays(numDays) {
  const formattedNumDays = numberFormatted(numDays);
  return numDays === 1 ? `${formattedNumDays} day` : `${formattedNumDays} days`;
}

const maxInches = computed(() => Math.max(...(props.normals?.months ?? []).map((month) => month.inches), 0));

function barHeight(inches) {
  // keep a sliver for a month with any snow, so it doesn't read as missing
  return maxInches.value > 0 ? Math.max((inches / maxInches.value) * 100, inches > 0 ? 2 : 0) : 0;
}

const stats = computed(() => {
  const { annual, snowiest, days, coverDays } = props.normals;
  const result = [{ label: "average snow per year", value: formatInches(annual) }];
  if (snowiest) {
    result.push({ label: `average snow in ${snowiest.month}`, value: formatInches(snowiest.inches) });
  }
  if (isNumber(days)) {
    result.push({ label: "average with over 1 in of snow", value: formatDays(days) });
  }
  if (isNumber(coverDays)) {
    result.push({ label: "with snow on the ground", value: formatDays(coverDays) });
  }
  return result;
});

// "November through March": the months that average an inch or more
const season = computed(() => {
  const months = (props.normals?.months ?? []).filter((month) => month.inches >= 1);
  if (months.length < 2) {
    return null;
  }
  return `${months[0].month} through ${months.at(-1).month}`;
});

function roundPercent(share) {
  return Math.round((share * 100) / 5) * 5;
}

// e.g. "Buffalo, in Erie County at 600 feet above sea level, gets about 95 inches of snow in an average year, more
// than 85% of towns in New York. Most of it falls from November through March, and January is the snowiest month,
// averaging 27 inches. In a typical winter, 26 days bring an inch or more of new snow, and snow covers the ground on
// about 71 days."
const description = computed(() => {
  const name = props.name;
  if (!name || !props.page) {
    return null;
  }
  const where = [];
  if (props.county && props.page.kind !== "county") {
    where.push(`in ${props.county.fullName.replace(`, ${props.state?.name}`, "")}`);
  }
  if (isNumber(props.page.elevation)) {
    where.push(`at ${numberFormatted(props.page.elevation)} feet above sea level`);
  }
  const subject = where.length ? `${name}, ${where.join(" ")},` : name;

  const normals = props.normals;
  if (!normals) {
    return where.length ? `${name} is ${where.join(" ")}.` : null;
  }
  if (!(normals.annual >= 1)) {
    return `${subject} rarely sees snow: it averages less than an inch a year.`;
  }

  let text = `${subject} gets about ${numberFormatted(normals.annual)} inches of snow in an average year`;
  const share = props.stateShare;
  if (isNumber(share) && props.state && props.page.kind !== "county") {
    if (share >= 0.6) {
      text += `, more than ${roundPercent(share)}% of towns in ${props.state.name}`;
    } else if (share <= 0.4) {
      text += `, less than most towns in ${props.state.name}`;
    } else {
      text += `, about as much as a typical town in ${props.state.name}`;
    }
  }
  text += ".";

  const snowiest = normals.snowiest;
  if (season.value && snowiest) {
    text += ` Most of it falls from ${season.value}, and ${snowiest.month} is the snowiest month, averaging ${numberFormatted(snowiest.inches)} inches.`;
  } else if (snowiest) {
    text += ` ${snowiest.month} is the snowiest month, averaging ${numberFormatted(snowiest.inches, { decimals: 1 })} inches.`;
  }

  const days = isNumber(normals.days) ? Math.round(normals.days) : null;
  const coverDays = isNumber(normals.coverDays) ? Math.round(normals.coverDays) : null;
  if (days >= 1 && coverDays >= 1) {
    text += ` In a typical winter, ${days} ${days === 1 ? "day brings" : "days bring"} an inch or more of new snow, and snow covers the ground on about ${coverDays} days.`;
  } else if (days >= 1) {
    text += ` In a typical winter, ${days} ${days === 1 ? "day brings" : "days bring"} an inch or more of new snow.`;
  }
  return text;
});

const stationDescription = computed(() => {
  const { name, distance, direction, elevation, inPlace } = props.normals.station;
  const height = isNumber(elevation) ? ` at an elevation of ${numberFormatted(elevation)} feet` : "";
  if (inPlace && props.name) {
    return `the ${name} weather station in ${props.name}${height}`;
  }
  const where = distance < 1 ? "less than a mile away" : `${numberFormatted(distance)} miles ${direction}`;
  return `the nearest weather station, ${name}, ${where}${height}`;
});
</script>
